import { useState, useMemo } from "react";
import { Row, Col, Container as Grid } from "react-bootstrap";
import { useFetch } from "hooks/api";
import { ProductsTable } from "components/core/blocks/ProductsTable";
import { ProductCard } from "modules/pricingv2/ProductCard";
import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import Loading from "components/core/blocks/Loading";
import { SetValue, sendUpdates } from "./components/SetValue";
import { DatesProvider } from "dates/DatesContext";
import { useDates } from "dates/DatesContext";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import DownloadReport from "modules/perf_reports/DownloadReport";
import { useAppSelector } from "../../redux/hooks";
import BuyBoxChart from "modules/pricingv2/cards/BuyBoxChart";
import { ButtonGroup } from "components/core/basic/ButtonGroup";
import { ExplainerBox } from "components/core/basic/ExplainerBox";

const Dashboard = () => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [valueType, setValueType] = useState("min_price");
  const [showSetValue, setShowSetValue] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState("all");

  const queryClient = useQueryClient();

  const breadCrumbItems = [{ name: "BuyBox", href: "/user/_pricing/buybox" }];

  const { group, date, start, end, preStart, preEnd } = useDates();

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    empty: true,
    group_by: "product_id",
    channel: "amazon",
    filters: "not_buybox",
    dt: date ? date.format("YYYY-MM-DD") : null,
  };

  const { data: productData, isLoading } = useFetch(
    ["product_report", marketPlace.marketPlace],
    `/api/data_report/product_report`,
    { ...filters },
    {
      default: [],
    },
  );

  const { data: percentageData, isLoading: percentageLoading } = useFetch(
    ["buy_box_percentage", marketPlace.marketPlace],
    `/pricing/buybox/percentage`,
    {
      market_place: marketPlace.marketPlace,
      start_date: start.format("YYYY-MM-DD"),
      end_date: end.format("YYYY-MM-DD"),
    },
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const productById = useMemo(() => {
    const ids = {};
    if (productData?.data?.products) {
      for (const product of productData.data.products) {
        ids[product.id] = product;
      }
    }
    return ids;
  }, [productData]);

  const bucketDescription = {
    all: "All products that have lost the Buy Box",
    REF: "Filters products that loss the Buy Box because the price is higher than the Reference Price.",
    CP: "Filters products that loss the Buy Box because the price is higher than a Competitive Price, from an external source.",
    DD: "Filters products that loss the Buy Box because the delivery date is longer compared to other sellers competing for the Buy Box.",
    HP: "Filters products that loss the Buy Box because the price is higher compared to other sellers competing for the Buy Box.",
    OOS: "Filters products that loss the Buy Box because the product is out of stock.",
    FM: "Filters products that loss the Buy Box because the seller is not a featured merchant.",
    UNK: "The reason for the loss of the Buy Box is unknown.",
  };

  const filteredBuckets = useMemo(() => {
    if (!productData?.data?.products) {
      return [];
    }

    const buttunBuckets = [
      { label: "All", value: "all", counter: 0, description: "All products that have lost the Buy Box" },
      {
        label: "Reference Price",
        value: "REF",
        counter: 0,
        description: "Filters products that loss the Buy Box because the price is higher than the Reference Price.",
      },
      {
        label: "Competitive Price",
        value: "CP",
        counter: 0,
        description:
          "Filters products that loss the Buy Box because the price is higher than a Competitive Price, from an external source.",
      },
      {
        label: "Delivery Date",
        value: "DD",
        counter: 0,
        description:
          "Filters products that loss the Buy Box because the delivery date is longer compared to other sellers competing for the Buy Box.",
      },
      {
        label: "High Price",
        value: "HP",
        counter: 0,
        description:
          "Filters products that loss the Buy Box because the price is higher compared to other sellers competing for the Buy Box.",
      },
      {
        label: "Out of Stock",
        value: "OOS",
        counter: 0,
        description: "Filters products that loss the Buy Box because the product is out of stock.",
      },
      {
        label: "No Featured Merchant",
        value: "FM",
        counter: 0,
        description: "Filters products that loss the Buy Box because the seller is not a featured merchant.",
      },
      {
        label: "Unknown",
        value: "UNK",
        counter: 0,
        description: "The reason for the loss of the Buy Box is unknown.",
      },
    ];

    const buckets = { all: 0 };
    for (const product of productData.data.products) {
      buckets["all"]++;
      const reason = product.buy_box_reason ? product.buy_box_reason : "UNK";
      if (!buckets[reason]) {
        buckets[reason] = 0;
      }
      buckets[reason]++;
    }

    // create a new variable called filteredBuckets and return only the buckets from buttonBuckets that have a value greater than 0
    const filteredBuckets = [];
    for (let i = 0; i < buttunBuckets.length; i++) {
      const b = buttunBuckets[i];
      if (buckets[b.value]) {
        b.counter = buckets[b.value];
        filteredBuckets.push(b);
      }
    }

    return filteredBuckets;
  }, [productData]);

  const productList = useMemo(() => {
    if (!productData?.data?.products) {
      return [];
    }

    const products = [];
    for (const product of productData.data.products) {
      let addProduct = selectedBucket === "all";
      if (product.buy_box_reason === null) {
        if (selectedBucket === "UNK") {
          addProduct = true;
        }
      } else if (!addProduct) {
        addProduct = product.buy_box_reason === selectedBucket;
      }
      if (addProduct) {
        products.push(product);
      }
    }

    return products;
  }, [productData, selectedBucket]);

  const ExpandedContent = ({ marketPlace, row }) => {
    return (
      <div style={{ backgroundColor: "#efefef" }} className="text-start">
        <ProductCard marketPlace={marketPlace} productId={row?.original?.product_id} />
      </div>
    );
  };

  const updateProducts = useMutation({
    mutationFn: async (updates) => {
      await sendUpdates("product_id", updates, marketPlace, queryClient);
    },
  });

  const columnSpecs = [
    {
      key: "product_price",
      compareKey: "price_delta",
      options: {
        checked: true,
        isNegative: true,
        noColor: true,
        metric: {
          id: "product_price",
          format: "currency",
          accessor: (d) => d?.product_price,
          name: "Current Price",
        },
      },
    },
    {
      key: "sales",
      compareKey: "sales_percent",
      options: { checked: true },
    },
    { key: "units", compareKey: "units_percent", options: { checked: true } },
    {
      key: "profit",
      compareKey: "profit_delta",
      options: { checked: true },
    },
  ];

  return (
    <>
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      <div className="main-content" style={{ backgroundColor: "#FFF" }}>
        <Grid fluid style={{ minHeight: "calc(100vh - 150px)" }}>
          <Row>
            <Col xs={12} style={{ borderRight: "1px solid #ddd" }}>
              {!percentageLoading && <BuyBoxChart percentageData={percentageData} />}

              {isLoading && <Loading fullPage={false} height={300} />}

              {!isLoading && (
                <>
                  <Row>
                    <Col xs={12} className="my-3">
                      <ButtonGroup
                        buttons={filteredBuckets}
                        value={selectedBucket}
                        onSelect={(values) => {
                          setSelectedBucket(values);
                        }}
                      />
                      <div className="mt-3">
                        <ExplainerBox>{bucketDescription[selectedBucket]}</ExplainerBox>
                      </div>
                    </Col>
                  </Row>
                  <ProductsTable
                    onBulkChange={async (changes, clearChanges) => {
                      const updates = {};
                      Object.entries(changes).forEach(([id, changes]) => {
                        updates[id] = Object.entries(changes)
                          .filter(([key]) => ["max_price", "min_price", "shipping_cost", "landed_cost"].includes(key))
                          .map(([key, value]) => [key, parseFloat(value)]);
                      });
                      updateProducts.mutate(updates, {
                        onSuccess: () => {
                          clearChanges?.();
                        },
                      });
                    }}
                    selectedProducts={selectedProducts}
                    marketPlace={marketPlace}
                    productData={productList}
                    ExpandedContent={ExpandedContent}
                    isSaving={updateProducts.isPending}
                    onSelection={setSelectedProducts}
                    hideCompareValue={true}
                    customColumns={columnSpecs}
                    showOffers={true}
                    bulkActionOptions={[
                      ...[
                        "Fixed Price",
                        "Clear Fixed Price",
                        "Minimum Price",
                        "Maximum Price",
                        "Shipping Cost",
                        "Landed Cost",
                      ].map((label) => ({
                        label: label === "Clear Fixed Price" ? label : `Set ${label}`,
                        action: () => {
                          setValueType(label);
                          setShowSetValue(true);
                        },
                      })),
                    ]}
                  />

                  <SetValue
                    showSetValue={showSetValue}
                    setShowSetValue={setShowSetValue}
                    valueType={valueType}
                    selectedProducts={selectedProducts}
                    productById={productById}
                    marketPlace={marketPlace}
                    setSelectedProducts={setSelectedProducts}
                  />
                </>
              )}
            </Col>
          </Row>
          {!isLoading && (
            <Row>
              <Col xs={12} className="d-flex justify-content-end mb-4 mt-4">
                <DownloadReport url_path={"product_report"} filters={{ ...filters }} report_dl={"product"} />
              </Col>
            </Row>
          )}
        </Grid>
      </div>
    </>
  );
};

const BuyBoxReport = () => {
  // const hasPriceOptimizationModule = useModuleEnabled("PRICE_OPTIMIZATION");
  // const user = useAppSelector((state) => state.user);
  // const amz_onboard = useAppSelector((state) => state.amz_onboard);

  // const location = useLocation();
  // const DYNAMIC_PRICING_SUBSCRIPTION =
  //   (((user.pricing_tier && user.pricing_tier !== "unknown") ||
  //     location?.state?.firstVisit ||
  //     location?.state?.onboardingComplete) &&
  //     (amz_onboard.org_status === "active" ||
  //       amz_onboard.org_status === "trial_active" ||
  //       amz_onboard.org_status === "demo_org")) ||
  //   hasPriceOptimizationModule ||
  //   checkTierAccess(user.tier, "pricing", amz_onboard.org_status);

  // if (!DYNAMIC_PRICING_SUBSCRIPTION) {
  //   return <PaymentDiscovery discoveryType={"advertising"} dashboardType="pricing" />;
  // }

  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export default BuyBoxReport;
